<template>
  <!-- Overlay variant: floating split pills that blur over a dark hero, then turn into a
       solid black bar once scrolled past the hero. Opt-in (homepage) so content pages that
       have no dark hero keep the plain solid bar below. -->
  <header v-if="overlay" class="HeaderOverlay" :class="{ 'is-solid': pastHero, 'is-open': isNavigationOpen, 'is-light': solidTheme === 'light' }">
    <div class="HeaderOverlay__inner">
      <div class="HeaderOverlay__pill HeaderOverlay__primary">
        <router-link :to="{ name: 'home' }" class="HeaderOverlay__logo" title="FormBeat">
          <Logo :light="logoLight" />
        </router-link>

        <!-- Desktop inline links (hidden on mobile; the dropdown menu below handles mobile). -->
        <nav class="HeaderOverlay__nav">
          <router-link
            v-for="link in links"
            :key="link.label"
            :to="link.to"
            :title="link.title"
            class="HeaderOverlay__link"
            :class="link.class"
            @click.native="toggleNavigation(false)"
          >
            {{ link.label }}
          </router-link>
        </nav>
      </div>

      <div class="HeaderOverlay__pill HeaderOverlay__secondary">
        <template v-if="!user">
          <router-link :to="{ name: 'signIn' }" class="HeaderOverlay__link md:inline hidden ml-6 mr-2" title="Sign in" @click.native="toggleNavigation(false)">
            Login
          </router-link>
          <router-link :to="{ name: 'join' }" title="Sign up" @click.native="toggleNavigation(false)">
            <AppButton primary small class="rounded-full">Join</AppButton>
          </router-link>
        </template>
        <router-link v-else :to="{ name: 'dashboard' }" title="Training Dashboard" @click.native="toggleNavigation(false)">
          <AppButton primary small class="rounded-full">Dashboard</AppButton>
        </router-link>

        <!-- Hamburger sits to the right of Join on mobile (hidden on desktop). -->
        <button class="HeaderOverlay__toggle md:hidden" type="button" :aria-label="isNavigationOpen ? 'Close menu' : 'Open menu'" @click="toggleNavigation()">
          <MenuIcon :open="isNavigationOpen" />
        </button>
      </div>
    </div>

    <!-- Mobile dropdown menu. Lives outside the blurred pills (whose backdrop-filter would
         otherwise become its containing block) so it can span nearly the full width. -->
    <nav class="HeaderOverlay__menu md:hidden">
      <router-link
        v-for="link in links"
        :key="link.label"
        :to="link.to"
        :title="link.title"
        class="HeaderOverlay__link"
        @click.native="toggleNavigation(false)"
      >
        {{ link.label }}
      </router-link>
      <router-link v-if="!user" :to="{ name: 'signIn' }" class="HeaderOverlay__link" title="Sign in" @click.native="toggleNavigation(false)">Login</router-link>
    </nav>
  </header>

  <div v-else class="Header" :class="{ 'is-open': isNavigationOpen }">
    <div class="Header__inner">
      <router-link :to="{ name: 'home' }" class="Header__logo" title="FormBeat">
        <Logo />
      </router-link>

      <nav class="Header__nav ml-12 mr-auto">
        <router-link v-for="link in links" :key="link.label" :to="link.to" :title="link.title" class="Header__link" :class="link.class">
          {{ link.label }}
        </router-link>
      </nav>

      <div class="Header__right">
        <div class="Header__actions">
          <router-link v-if="!user" :to="{ name: 'signIn' }" class="Header__link Header__signin" title="Sign in">Login</router-link>
          <router-link v-if="!user" :to="{ name: 'join' }" title="Sign up">
            <AppButton primary small class="rounded-full">Join</AppButton>
          </router-link>
          <router-link v-else :to="{ name: 'dashboard' }" title="Training Dashboard">
            <AppButton primary small class="rounded-full">Dashboard</AppButton>
          </router-link>

          <button class="Header__toggle md:hidden" type="button" :aria-label="isNavigationOpen ? 'Close menu' : 'Open menu'" @click="toggleNavigation()">
            <MenuIcon :open="isNavigationOpen" />
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile dropdown: same animated near-full-width pattern as the overlay nav, light themed. -->
    <nav class="Header__menu md:hidden">
      <router-link v-for="link in links" :key="link.label" :to="link.to" :title="link.title" class="Header__menu-link" @click.native="toggleNavigation(false)">
        {{ link.label }}
      </router-link>
      <router-link v-if="!user" :to="{ name: 'signIn' }" class="Header__menu-link" title="Sign in" @click.native="toggleNavigation(false)">Login</router-link>
    </nav>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import MenuIcon from 'components/MenuIcon'

export default {
  components: {
    MenuIcon
  },

  props: {
    // Opt into the floating-pill nav that blurs over a dark hero and turns solid on scroll.
    overlay: {
      type: Boolean,
      default: false
    },
    // Appearance of the pills once scrolled past the hero: 'dark' (deep-blue + white text)
    // or 'light' (white + dark text).
    solidTheme: {
      type: String,
      default: 'dark',
      validator: (v) => ['dark', 'light'].includes(v)
    }
  },

  data() {
    return {
      isNavigationOpen: false,
      pastHero: false
    }
  },

  computed: {
    ...mapGetters('profile', ['user']),

    // White logo over the hero (always) and on the dark solid theme; dark logo only once the
    // light solid theme has kicked in past the hero.
    logoLight() {
      return !this.pastHero || this.solidTheme !== 'light'
    },

    links() {
      return [
        { label: 'Features', title: 'Features', to: { name: 'home', query: { section: 'features' } } },
        { label: 'Training Plans', title: 'Adaptive Cycling Training Plans', to: { name: 'cyclingTrainingPlans' } },
        { label: 'AI Coach', title: 'AI Cycling Coach', to: { name: 'aiCyclingCoach' } },
        { label: 'Mobile App', title: 'Get Mobile App', to: { name: 'mobile' }, class: 'lg:block hidden' },
        { label: 'Tools', title: 'Cycling Tools and Calculators', to: { name: 'tools' } },
        { label: 'Changelog', title: 'Changelog', to: { name: 'changelog' }, class: 'lg:block hidden' },
        { label: 'Help', title: 'Help & Support', to: { name: 'help' } }
      ]
    }
  },

  mounted() {
    if (this.overlay) {
      window.addEventListener('scroll', this.onScroll, { passive: true })
      this.onScroll()
    }
  },

  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll)
  },

  methods: {
    ...mapActions('profile', ['signOut']),

    // Switch to the solid-black bar once the hero has scrolled past. Measure the hero so the
    // threshold tracks its real height; fall back to the viewport if it isn't on the page.
    onScroll() {
      const hero = document.querySelector('.Hero')
      const threshold = (hero ? hero.offsetHeight : window.innerHeight) - 80
      this.pastHero = window.scrollY > threshold
    },

    toggleNavigation(isOpen) {
      this.isNavigationOpen = isOpen === undefined ? !this.isNavigationOpen : isOpen
    },

    async logout() {
      await this.signOut()
      window.location.reload()
    }
  }
}
</script>

<style media="screen" lang="postcss" scoped>
/* ---- Overlay variant (floating pills over hero -> solid black bar) ---- */
.HeaderOverlay {
  @apply fixed inset-x-0 top-0 z-50 select-none;
  padding: 1.25rem 1rem;
  transition: background-color 0.35s ease, padding 0.35s ease;
}

.HeaderOverlay__inner {
  /* items-stretch makes the left (logo/links) pill match the height of the right pill, whose
     button is taller — so both pills stay consistent without hardcoding padding. */
  @apply flex items-stretch justify-between mx-auto;
  /* Keep in sync with the homepage section cap (.Home) so the nav aligns with the content. */
  max-width: 90rem;
}

.HeaderOverlay__pill {
  @apply flex items-center;
  padding: 0.4rem 1.25rem;
  background: rgba(50, 50, 0, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 9999px;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  transition: background-color 0.35s ease, border-color 0.35s ease, backdrop-filter 0.35s ease;
}

/* Pad the outer (column) side of each pill by the same 2rem gutter the content sections use,
   so the logo and the Join button line up with the content column edges. */
.HeaderOverlay__primary {
  @apply space-x-8;
  padding-left: 1.25rem;
  padding-right: 2rem;
}

.HeaderOverlay__secondary {
  /* gap (not space-x) so a hidden child — Login on mobile — doesn't leave a phantom margin. */
  gap: 1rem;
  /* Balanced inner padding: only the right edge anchors to the content column, and a lone
     CTA (the logged-in Dashboard button) looks off-center with a large left pad. */
  padding-right: 0.4rem;
  padding-left: 0.4rem;
}

.HeaderOverlay__logo {
  @apply flex items-center;
}

.HeaderOverlay__logo ::v-deep img {
  width: 132px;
}

.HeaderOverlay__nav {
  @apply flex items-center space-x-6;
}

.HeaderOverlay__link {
  @apply text-base font-medium text-white no-underline;
  opacity: 0.85;
  transition: opacity 0.2s ease, color 0.35s ease;
}

.HeaderOverlay__link:hover {
  opacity: 1;
}

.HeaderOverlay__toggle {
  /* No `display` here — that would override the md:hidden utility and show it on desktop.
     The flex centering is applied in the mobile media query where the toggle is shown. */
  @apply items-center justify-center flex-shrink-0 ml-1 mr-2 text-2xl leading-none text-white bg-transparent cursor-pointer;
  /* Fixed square so the = and ✕ glyphs (different widths) don't shift the layout on toggle. */
  width: 1.75rem;
  height: 1.75rem;
  appearance: none;
}

.HeaderOverlay__toggle:focus {
  outline: none;
  box-shadow: none;
}

/* Past the hero, the blur fills into a solid pill. Two themes (switch via the `solidTheme`
   prop): default 'dark' = deep-blue pill + white text; 'light' = white pill + dark text. */
.HeaderOverlay.is-solid .HeaderOverlay__pill {
  background: #112216;
  border-color: transparent;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
}

/* Light theme overrides */
.HeaderOverlay.is-solid.is-light .HeaderOverlay__pill {
  background: #ffffff;
  border-color: rgba(0, 0, 0, 0.06);
  box-shadow: 0 6px 24px -10px rgba(0, 0, 0, 0.25);
}

.HeaderOverlay.is-solid.is-light .HeaderOverlay__link,
.HeaderOverlay.is-solid.is-light .HeaderOverlay__toggle {
  color: #111827;
}

@media only screen and (max-width: theme('screens.md')) {
  /* Tighter pills + smaller logo so the two pills fit comfortably on a phone. */
  .HeaderOverlay {
    padding: 0.75rem;
  }
  .HeaderOverlay__pill {
    padding: 0.4rem 0.85rem;
  }
  .HeaderOverlay__primary {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }
  /* Right pill holds [Join, =] on mobile (Login hidden). */
  .HeaderOverlay__secondary {
    gap: 0.6rem;
    padding-left: 0.4rem;
    padding-right: 0.4rem;
  }
  .HeaderOverlay__logo ::v-deep img {
    width: 132px;
  }

  /* The desktop inline links live inside the blurred pill; hidden on mobile. */
  .HeaderOverlay__nav {
    display: none;
  }
  /* Center the glyph (only on mobile, where the toggle is visible). */
  .HeaderOverlay__toggle {
    @apply flex items-center justify-center;
  }

  /* Once scrolled past the hero, the floating pills give way to a full-width solid black bar.
     The container's vertical padding collapses to 0 (animated) so the bar tightens up. */
  .HeaderOverlay.is-solid {
    background: #000;
    box-shadow: 0 1px 0 rgba(255, 255, 255, 0.06);
    padding-top: 0;
    padding-bottom: 0;
  }
  .HeaderOverlay.is-solid .HeaderOverlay__pill {
    background: transparent;
    border-color: transparent;
    box-shadow: none;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }

  /* Near-full-width dropdown that slides + fades in (animated via opacity/transform so it can
     transition — display:none can't). Lives outside the blurred pills, so it spans the header. */
  .HeaderOverlay__menu {
    @apply absolute flex-col items-stretch p-3 shadow-lg;
    display: flex;
    left: 0.6rem;
    right: 0.6rem;
    top: 100%;
    margin-top: 0.6rem;
    gap: 0.1rem;
    border-radius: 1.25rem;
    background: rgba(10, 12, 10, 0.94);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    opacity: 0;
    transform: translateY(-10px) scale(0.98);
    transform-origin: top center;
    pointer-events: none;
    transition: opacity 0.28s cubic-bezier(0.22, 1, 0.36, 1), transform 0.28s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .HeaderOverlay.is-open .HeaderOverlay__menu {
    opacity: 1;
    transform: translateY(0) scale(1);
    pointer-events: auto;
  }
  .HeaderOverlay__menu .HeaderOverlay__link {
    @apply px-3 py-3 text-lg text-white rounded-lg;
    opacity: 1;
  }
  .HeaderOverlay__menu .HeaderOverlay__link:active {
    background: rgba(255, 255, 255, 0.06);
  }
}

/* ---- Default variant: static light bar with the same components/UX as the overlay nav ---- */
/* Bar (bg + border) is full-width; the inner content is capped to the same 1440px column. */
.Header {
  @apply relative z-40 bg-white border-b select-none;
}

.Header__inner {
  @apply flex items-center justify-between h-16 px-8 mx-auto;
  max-width: 90rem;
}

.Header__logo {
  @apply flex items-center;
}

.Header__right {
  @apply flex items-center;
  gap: 1.5rem;
}

.Header__nav {
  @apply flex items-center space-x-6;
}

.Header__actions {
  @apply flex items-center;
  gap: 1.5rem;
}

.Header__link {
  @apply text-base font-medium no-underline;
}

.Header__toggle {
  @apply flex-shrink-0 text-2xl leading-none cursor-pointer;
  width: 1.75rem;
  height: 1.75rem;
  appearance: none;
  background: transparent;
}
.Header__toggle:focus {
  outline: none;
  box-shadow: none;
}

@media only screen and (max-width: theme('screens.md')) {
  /* Bar shows logo + [Sign up, =]; links + Sign in move into the dropdown. */
  .Header__nav,
  .Header__signin {
    display: none;
  }
  .Header__actions {
    gap: 0.85rem;
  }
  .Header__toggle {
    @apply flex items-center justify-center;
  }

  /* Animated near-full-width dropdown (light), matching the overlay menu's motion. */
  .Header__menu {
    @apply absolute flex-col items-stretch p-3 shadow-lg;
    display: flex;
    left: 0.75rem;
    right: 0.75rem;
    top: 100%;
    margin-top: 0.5rem;
    gap: 0.1rem;
    border-radius: 1rem;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.06);
    opacity: 0;
    transform: translateY(-10px) scale(0.98);
    transform-origin: top center;
    pointer-events: none;
    transition: opacity 0.28s cubic-bezier(0.22, 1, 0.36, 1), transform 0.28s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .Header.is-open .Header__menu {
    opacity: 1;
    transform: translateY(0) scale(1);
    pointer-events: auto;
  }
  .Header__menu-link {
    @apply block px-3 py-3 text-lg no-underline rounded-lg;
  }
  .Header__menu-link:active {
    background: rgba(0, 0, 0, 0.04);
  }
}
</style>
